import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import BarMenu from "../BarMenu";
import api from "../../utils/api";
import "./styles.scss";

// ─── helpers ────────────────────────────────────────────────────────────────

const fmtPct = (n) => `${n ?? 0}%`;
const fmtNum = (n) => (n ?? 0).toLocaleString('pt-BR');

function fmtDate(raw) {
    if (!raw) return '—';
    const d = new Date(raw);
    if (isNaN(d)) return String(raw);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const MM = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${hh}:${mm} — ${dd}/${MM}/${yyyy}`;
}

const TICKET_PAGE_SIZE = 50;

function pctColor(pct) {
    if (pct >= 80) return '#10b981';
    if (pct >= 50) return '#f59e0b';
    return '#ef4444';
}

function applyDateRange(creationDates, from, to) {
    if (!from && !to) return null; // null = no date filter
    const d0 = from ? new Date(from + 'T00:00:00') : null;
    const d1 = to   ? new Date(to   + 'T23:59:59') : null;
    return creationDates.filter(ds => {
        const d = new Date(ds);
        if (d0 && d < d0) return false;
        if (d1 && d > d1) return false;
        return true;
    }).length;
}

function applyFiltersToStats(stats, stockFilter, dateFrom, dateTo) {
    let baseTotal = stats.total;
    let baseBipados = stats.bipados;

    // date range: recalculate proportion
    if (dateFrom || dateTo) {
        const count = applyDateRange(stats.creationDates || [], dateFrom, dateTo);
        if (count === 0) return null;
        const prop = count / stats.total;
        baseTotal = count;
        baseBipados = Math.round(stats.bipados * prop);
    }

    // stock filter: recalculate proportion
    if (stockFilter && stats.stockStatus) {
        const stockTotal = stats.stockStatus[stockFilter] || 0;
        if (stockTotal === 0) return null;
        const totalSum = Object.values(stats.stockStatus).reduce((a, b) => a + b, 0);
        const prop = totalSum > 0 ? stockTotal / totalSum : 0;
        baseTotal = Math.round(baseTotal * prop);
        baseBipados = Math.round(baseBipados * prop);
    }

    const nao = baseTotal - baseBipados;
    const pct = baseTotal > 0 ? Math.round((baseBipados / baseTotal) * 100) : 0;
    return { ...stats, total: baseTotal, bipados: baseBipados, naBipados: nao, percentualBipado: pct };
}

// ─── ProgressBar ────────────────────────────────────────────────────────────

function ProgressBar({ value, total, small }) {
    const pct = total > 0 ? Math.round((value / total) * 100) : 0;
    const color = pctColor(pct);
    return (
        <div className={`prog-bar ${small ? 'small' : ''}`}>
            <div className="prog-track">
                <div className="prog-fill" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="prog-pct" style={{ color }}>{pct}%</span>
        </div>
    );
}

// ─── SortableHeader ─────────────────────────────────────────────────────────

function Th({ label, col, sort, onSort }) {
    const active = sort.key === col;
    return (
        <th className={`sortable ${active ? 'sorted' : ''}`} onClick={() => onSort(col)}>
            {label}
            <span className="sort-arrow">{active ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : ' ↕'}</span>
        </th>
    );
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function Analytics() {
    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [activeTab, setActiveTab] = useState('resumo');
    const [search, setSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [stockFilter, setStockFilter] = useState(null);
    const [sort, setSort] = useState({ key: 'total', dir: 'desc' });
    const [nlSearch, setNlSearch] = useState('');
    const [nlPage, setNlPage] = useState(0);
    const NL_PAGE_SIZE = 50;

    // ── Tickets tab state ─────────────────────────────────────────────────
    const [ticketSearch, setTicketSearch]     = useState('');
    const [ticketPage,   setTicketPage]       = useState(0);
    const [ticketsList,  setTicketsList]      = useState([]);
    const [ticketsTotal, setTicketsTotal]     = useState(0);
    const [ticketsLoading, setTicketsLoading] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);   // { conjunto, item }
    const [detailLoading, setDetailLoading]   = useState(false);
    const ticketSearchRef = useRef('');

    useEffect(() => { fetchAnalytics(); }, []);
    useEffect(() => { setNlPage(0); }, [nlSearch]);

    const fetchAnalytics = async () => {
        try {
            setLoading(true);
            setError(false);
            const res = await api.get('/analytics');
            if (res.data.success) setAnalytics(res.data.data);
            else setError(true);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    const fetchTickets = useCallback(async (search, page) => {
        try {
            setTicketsLoading(true);
            const res = await api.get('/analytics/tickets', { params: { search, page, limit: TICKET_PAGE_SIZE } });
            if (res.data.success) {
                setTicketsList(res.data.data.items);
                setTicketsTotal(res.data.data.total);
            }
        } catch { /* ignore */ } finally {
            setTicketsLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // fetch tickets when tab activates or pagination changes
    useEffect(() => {
        if (activeTab === 'tickets') fetchTickets(ticketSearch, ticketPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, ticketPage]);

    // debounced search for tickets
    useEffect(() => {
        if (activeTab !== 'tickets') return;
        ticketSearchRef.current = ticketSearch;
        const t = setTimeout(() => {
            if (ticketSearchRef.current === ticketSearch) {
                setTicketPage(0);
                fetchTickets(ticketSearch, 0);
            }
        }, 350);
        return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketSearch]);

    const openTicketDetail = async (awb) => {
        try {
            setDetailLoading(true);
            setSelectedTicket({ loading: true });
            const res = await api.get(`/analytics/tickets/${awb}`);
            if (res.data.success) setSelectedTicket(res.data.data);
            else setSelectedTicket(null);
        } catch {
            setSelectedTicket(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const handleSort = (col) => {
        setSort(prev =>
            prev.key === col
                ? { key: col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                : { key: col, dir: 'desc' }
        );
    };

    const clearFilters = () => {
        setSearch('');
        setDateFrom('');
        setDateTo('');
        setStockFilter(null);
    };

    const hasFilters = search || dateFrom || dateTo || stockFilter;

    // ─── build sorted+filtered rows for data tabs ──────────────────────────

    const buildRows = (data) => {
        if (!data) return [];

        return Object.entries(data)
            .filter(([key, stats]) => !search ||
                key.toLowerCase().includes(search.toLowerCase()) ||
                (stats.awbs && stats.awbs.some(awb => awb.toUpperCase().includes(search.toUpperCase())))
            )
            .map(([key, stats]) => {
                const filtered = applyFiltersToStats(stats, stockFilter, dateFrom, dateTo);
                return filtered ? [key, filtered] : null;
            })
            .filter(Boolean)
            .sort(([, a], [, b]) => {
                const va = sort.key === 'pct' ? a.percentualBipado
                    : sort.key === 'bipados' ? a.bipados
                    : sort.key === 'nao' ? a.naBipados
                    : a.total;
                const vb = sort.key === 'pct' ? b.percentualBipado
                    : sort.key === 'bipados' ? b.bipados
                    : sort.key === 'nao' ? b.naBipados
                    : b.total;
                return sort.dir === 'asc' ? va - vb : vb - va;
            });
    };

    // ─── filtered "Não Localizados" ────────────────────────────────────────

    const nlItems = useMemo(() => {
        if (!analytics?.naoLocalizados) return [];
        const q = nlSearch.toLowerCase();
        return analytics.naoLocalizados.filter(item =>
            !q || [item.awb, item.subject, item.shipper, item.route, item.ticketStatus]
                .some(v => v?.toLowerCase().includes(q))
        );
    }, [analytics, nlSearch]);

    const nlPaged = nlItems.slice(nlPage * NL_PAGE_SIZE, (nlPage + 1) * NL_PAGE_SIZE);
    const nlTotalPages = Math.ceil(nlItems.length / NL_PAGE_SIZE);

    // ─── top-5 for "Resumo" tab ─────────────────────────────────────────────

    const top5 = (data) => {
        if (!data) return [];
        return Object.entries(data)
            .sort(([, a], [, b]) => b.total - a.total)
            .slice(0, 5);
    };

    // ─── RENDER ─────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <div id="Analytics">
                <BarMenu />
                <section className="analytics-section">
                    <div className="loading-state">
                        <div className="spinner" />
                        <p>Carregando estatísticas...</p>
                    </div>
                </section>
            </div>
        );
    }

    if (error || !analytics) {
        return (
            <div id="Analytics">
                <BarMenu />
                <section className="analytics-section">
                    <div className="error-state">
                        <span>⚠️</span>
                        <p>Erro ao carregar estatísticas</p>
                        <button className="btn-refresh" onClick={fetchAnalytics}>Tentar novamente</button>
                    </div>
                </section>
            </div>
        );
    }

    const g = analytics.geral;
    const bipPct = g.percentualBipado;

    // ─── renderDataTable ─────────────────────────────────────────────────────

    const renderDataTable = (data, colLabel) => {
        const rows = buildRows(data);
        if (rows.length === 0) {
            return (
                <div className="empty-state">
                    <span>🔍</span>
                    <p>{hasFilters ? 'Nenhum resultado para os filtros aplicados.' : 'Sem dados disponíveis.'}</p>
                </div>
            );
        }

        return (
            <div className="data-table-wrap">
                <div className="table-meta">{rows.length} {rows.length === 1 ? 'registro' : 'registros'}</div>
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <Th label={colLabel} col="name" sort={sort} onSort={handleSort} />
                                <Th label="Total" col="total" sort={sort} onSort={handleSort} />
                                <Th label="Bipados" col="bipados" sort={sort} onSort={handleSort} />
                                <Th label="Não Bip." col="nao" sort={sort} onSort={handleSort} />
                                {colLabel !== 'Status' && <th className="th-stock">Bipados / Estoque</th>}
                                <Th label="Taxa" col="pct" sort={sort} onSort={handleSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(([key, stats]) => (
                                <tr key={key}>
                                    <td className="col-name">
                                        <span className="name-text" title={key}>{key}</span>
                                    </td>
                                    <td className="col-num">{fmtNum(stats.total)}</td>
                                    <td className="col-num green">{fmtNum(stats.bipados)}</td>
                                    <td className="col-num orange">{fmtNum(stats.naBipados)}</td>
                                    {colLabel !== 'Status' && (
                                        <td className="col-stock">
                                            {stats.stockStatus && stats.stockBipados && (
                                                <div className="stock-pills">
                                                    {stats.stockStatus['Fora Estoque'] > 0 && (
                                                        <span className="pill pill-out" title={`Fora Estoque: ${stats.stockBipados['Fora Estoque']} bipados de ${stats.stockStatus['Fora Estoque']}`}>
                                                            📦 <span className="pill-ratio">{fmtNum(stats.stockBipados['Fora Estoque'])}/{fmtNum(stats.stockStatus['Fora Estoque'])}</span>
                                                        </span>
                                                    )}
                                                    {stats.stockStatus['Em Estoque'] > 0 && (
                                                        <span className="pill pill-in" title={`Em Estoque: ${stats.stockBipados['Em Estoque']} bipados de ${stats.stockStatus['Em Estoque']}`}>
                                                            ✓ <span className="pill-ratio">{fmtNum(stats.stockBipados['Em Estoque'])}/{fmtNum(stats.stockStatus['Em Estoque'])}</span>
                                                        </span>
                                                    )}
                                                    {(stats.stockStatus['Outros'] || 0) > 0 && (
                                                        <span className="pill pill-other" title={`Outros: ${stats.stockBipados['Outros']} bipados de ${stats.stockStatus['Outros']}`}>
                                                            ? <span className="pill-ratio">{fmtNum(stats.stockBipados['Outros'])}/{fmtNum(stats.stockStatus['Outros'])}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    )}
                                    <td className="col-prog">
                                        <ProgressBar value={stats.bipados} total={stats.total} small />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ─── renderResumo ────────────────────────────────────────────────────────

    const renderResumo = () => (
        <div className="resumo-wrap">
            <div className="resumo-progress-card">
                <h3>Progresso Geral de Localizados</h3>
                <div className="big-prog">
                    <ProgressBar value={g.totalBipados} total={g.totalConjuntos} />
                    <p className="big-prog-label">
                        {fmtNum(g.totalBipados)} de {fmtNum(g.totalConjuntos)} itens bipados
                    </p>
                </div>
            </div>

            <div className="resumo-grid">
                <div className="resumo-block">
                    <h4>Top 5 — Por Assunto</h4>
                    <div className="mini-table">
                        {top5(analytics.porAssunto).map(([key, stats]) => (
                            <div className="mini-row" key={key}>
                                <span className="mini-name" title={key}>{key}</span>
                                <span className="mini-total">{fmtNum(stats.bipados)} / {fmtNum(stats.total)}</span>
                                <ProgressBar value={stats.bipados} total={stats.total} small />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="resumo-block">
                    <h4>Top 5 — Por Embarcador</h4>
                    <div className="mini-table">
                        {top5(analytics.porEmbarcador).map(([key, stats]) => (
                            <div className="mini-row" key={key}>
                                <span className="mini-name" title={key}>{key}</span>
                                <span className="mini-total">{fmtNum(stats.bipados)} / {fmtNum(stats.total)}</span>
                                <ProgressBar value={stats.bipados} total={stats.total} small />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="resumo-block">
                    <h4>Top 5 — Por Rota</h4>
                    <div className="mini-table">
                        {top5(analytics.porRota).map(([key, stats]) => (
                            <div className="mini-row" key={key}>
                                <span className="mini-name" title={key}>{key}</span>
                                <span className="mini-total">{fmtNum(stats.bipados)} / {fmtNum(stats.total)}</span>
                                <ProgressBar value={stats.bipados} total={stats.total} small />
                            </div>
                        ))}
                    </div>
                </div>

                <div className="resumo-block">
                    <h4>Por Status de Estoque</h4>
                    <div className="mini-table">
                        {Object.entries(analytics.porStockStatus)
                            .sort(([, a], [, b]) => b.total - a.total)
                            .map(([key, stats]) => (
                                <div className="mini-row" key={key}>
                                    <span className="mini-name" title={key}>{key}</span>
                                    <span className="mini-total">{fmtNum(stats.bipados)} / {fmtNum(stats.total)}</span>
                                    <ProgressBar value={stats.bipados} total={stats.total} small />
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        </div>
    );

    // ─── renderNaoLocalizados ───────────────────────────────────────────────

    const renderNaoLocalizados = () => (
        <div className="nl-wrap">
            <div className="nl-info">
                <p>
                    Itens com <strong>Em Estoque</strong>, ainda não bipados e sem status que indique
                    saída (em rota, entrega realizada, devolução, etc.).
                </p>
            </div>

            <div className="nl-search-row">
                <input
                    className="nl-search"
                    type="text"
                    placeholder="Buscar por AWB, assunto, embarcador, rota, status..."
                    value={nlSearch}
                    onChange={e => setNlSearch(e.target.value)}
                />
                {nlSearch && (
                    <button className="btn-clear-inline" onClick={() => setNlSearch('')}>✕</button>
                )}
                <span className="nl-count">{fmtNum(nlItems.length)} item{nlItems.length !== 1 ? 's' : ''}</span>
            </div>

            {nlItems.length === 0 ? (
                <div className="empty-state">
                    <span>✅</span>
                    <p>{nlSearch ? 'Nenhum resultado para a busca.' : 'Nenhum item não localizado!'}</p>
                </div>
            ) : (
                <>
                    <div className="table-scroll">
                        <table className="data-table nl-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>AWB</th>
                                    <th>Assunto</th>
                                    <th>Embarcador</th>
                                    <th>Rota</th>
                                    <th>Etapa</th>
                                    <th>Status Ticket</th>
                                    <th>Descrição Status</th>
                                    <th>Último Status / Criação</th>
                                    <th>Aging</th>
                                </tr>
                            </thead>
                            <tbody>
                                {nlPaged.map((item, i) => {
                                    const hasIcsDate = !!item.ticketLastStatusDate;
                                    const dateToShow = hasIcsDate ? item.ticketLastStatusDate : item.creationDate;
                                    return (
                                        <tr key={item.awb}>
                                            <td className="col-idx">{nlPage * NL_PAGE_SIZE + i + 1}</td>
                                            <td className="col-awb">{item.awb}</td>
                                            <td className="col-subject" title={item.subject}>{item.subject || '—'}</td>
                                            <td>{item.shipper || '—'}</td>
                                            <td>{item.route || '—'}</td>
                                            <td>{item.stage || '—'}</td>
                                            <td className="col-status" title={item.ticketStatus}>{item.ticketStatus || '—'}</td>
                                            <td className="col-desc" title={item.statusDescription}>{item.statusDescription || '—'}</td>
                                            <td className="col-date">
                                                {dateToShow
                                                    ? <>
                                                        {new Date(dateToShow).toLocaleDateString('pt-BR')}
                                                        <span className={`date-badge ${hasIcsDate ? 'ics' : 'ticket'}`}>
                                                            {hasIcsDate ? 'ICS' : 'Ticket'}
                                                        </span>
                                                      </>
                                                    : '—'}
                                            </td>
                                            <td className="col-aging">
                                                {item.agingDays != null ? `${item.agingDays}d` : '—'}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {nlTotalPages > 1 && (
                        <div className="pagination">
                            <button disabled={nlPage === 0} onClick={() => setNlPage(0)}>«</button>
                            <button disabled={nlPage === 0} onClick={() => setNlPage(p => p - 1)}>‹</button>
                            <span className="page-info">
                                Página {nlPage + 1} de {nlTotalPages}
                                {' '}({fmtNum(nlItems.length)} itens)
                            </span>
                            <button disabled={nlPage >= nlTotalPages - 1} onClick={() => setNlPage(p => p + 1)}>›</button>
                            <button disabled={nlPage >= nlTotalPages - 1} onClick={() => setNlPage(nlTotalPages - 1)}>»</button>
                        </div>
                    )}
                </>
            )}
        </div>
    );

    // ─── renderTickets ───────────────────────────────────────────────────────

    const renderTickets = () => {
        const totalPages = Math.ceil(ticketsTotal / TICKET_PAGE_SIZE);

        return (
            <div className="tickets-wrap nl-wrap">
                {/* Search bar */}
                <div className="tickets-search-bar">
                    <div className="search-input-wrap">
                        <span className="search-icon">🔍</span>
                        <input
                            type="text"
                            placeholder="Buscar por AWB..."
                            value={ticketSearch}
                            onChange={e => setTicketSearch(e.target.value.toUpperCase())}
                            autoFocus
                        />
                        {ticketSearch && (
                            <button className="btn-clear-inline" onClick={() => setTicketSearch('')}>✕</button>
                        )}
                    </div>
                    <span className="tickets-count">
                        {ticketsLoading ? 'Carregando...' : `${fmtNum(ticketsTotal)} ticket${ticketsTotal !== 1 ? 's' : ''}`}
                    </span>
                </div>

                {/* Table */}
                <div className="table-scroll">
                    <table className="data-table tickets-table">
                        <thead>
                            <tr>
                                <th>AWB</th>
                                <th>Assunto</th>
                                <th>Embarcador</th>
                                <th>Rota</th>
                                <th>Estoque</th>
                                <th>Status Ticket</th>
                                <th>Criação</th>
                                <th>Bipado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ticketsLoading && ticketsList.length === 0 ? (
                                <tr><td colSpan={8} className="loading-row">Carregando...</td></tr>
                            ) : ticketsList.length === 0 ? (
                                <tr><td colSpan={8} className="loading-row">Nenhum ticket encontrado.</td></tr>
                            ) : ticketsList.map(t => (
                                <tr key={t.awb} className="ticket-row" onClick={() => openTicketDetail(t.awb)}>
                                    <td className="td-awb">{t.awb}</td>
                                    <td className="td-text">{t.subject || '—'}</td>
                                    <td className="td-text">{t.shipper || '—'}</td>
                                    <td className="td-text">{t.route || '—'}</td>
                                    <td>
                                        <span className={`stock-badge ${t.stockStatus === 'Em Estoque' ? 'in' : t.stockStatus === 'Fora Estoque' ? 'out' : 'other'}`}>
                                            {t.stockStatus || '—'}
                                        </span>
                                    </td>
                                    <td className="td-text td-status">{t.ticketStatus || '—'}</td>
                                    <td className="td-date">{t.creationDate ? new Date(t.creationDate).toLocaleDateString('pt-BR') : '—'}</td>
                                    <td>
                                        <span className={`bipado-badge ${t.bipado ? 'sim' : 'nao'}`}>
                                            {t.bipado ? 'SIM' : 'NÃO'}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button disabled={ticketPage === 0} onClick={() => setTicketPage(0)}>«</button>
                        <button disabled={ticketPage === 0} onClick={() => setTicketPage(p => p - 1)}>‹</button>
                        <span className="page-info">
                            Página {ticketPage + 1} de {totalPages} ({fmtNum(ticketsTotal)} itens)
                        </span>
                        <button disabled={ticketPage >= totalPages - 1} onClick={() => setTicketPage(p => p + 1)}>›</button>
                        <button disabled={ticketPage >= totalPages - 1} onClick={() => setTicketPage(totalPages - 1)}>»</button>
                    </div>
                )}

                {/* Detail panel overlay */}
                {selectedTicket && (
                    <div className="ticket-overlay" onClick={() => setSelectedTicket(null)}>
                        <div className="ticket-detail-panel" onClick={e => e.stopPropagation()}>
                            <button className="detail-close" onClick={() => setSelectedTicket(null)}>✕</button>

                            {selectedTicket.loading || detailLoading ? (
                                <div className="detail-loading">Carregando...</div>
                            ) : (() => {
                                const c = selectedTicket.conjunto;
                                const item = selectedTicket.item;
                                const foiBipado = !!item;

                                return (
                                    <>
                                        <div className="detail-header">
                                            <span className="detail-awb">{c.awb}</span>
                                            <span className={`bipado-badge ${foiBipado ? 'sim' : 'nao'}`}>
                                                {foiBipado ? 'BIPADO' : 'NÃO BIPADO'}
                                            </span>
                                            <span className={`stock-badge ${c.stockStatus === 'Em Estoque' ? 'in' : c.stockStatus === 'Fora Estoque' ? 'out' : 'other'}`}>
                                                {c.stockStatus || '—'}
                                            </span>
                                        </div>

                                        <div className="detail-sections">
                                            <div className="detail-section">
                                                <h4>Identificação</h4>
                                                <dl>
                                                    <dt>Assunto</dt><dd>{c.subject || '—'}</dd>
                                                    <dt>Origem</dt><dd>{c.origin || '—'}</dd>
                                                    <dt>Tipo de Rota</dt><dd>{c.routeType || '—'}</dd>
                                                    <dt>Grupo Responsável</dt><dd>{c.responsibleGroup || '—'}</dd>
                                                    <dt>Criação do Ticket</dt><dd>{fmtDate(c.creationDate)}</dd>
                                                </dl>
                                            </div>

                                            <div className="detail-section">
                                                <h4>Logística</h4>
                                                <dl>
                                                    <dt>Embarcador</dt><dd>{c.shipper || '—'}</dd>
                                                    <dt>Rota</dt><dd>{c.route || '—'}</dd>
                                                    <dt>Etapa</dt><dd>{c.stage || '—'}</dd>
                                                    <dt>Aging</dt><dd>{c.agingDays != null ? `${c.agingDays} dias` : '—'}{c.agingDescription ? ` — ${c.agingDescription}` : ''}</dd>
                                                </dl>
                                            </div>

                                            <div className="detail-section">
                                                <h4>Status do Ticket</h4>
                                                <dl>
                                                    <dt>Status</dt><dd>{c.ticketStatus || '—'}</dd>
                                                    <dt>Data Último Status (ICS)</dt>
                                                    <dd>
                                                        {c.ticketLastStatusDate
                                                            ? <><span className="date-badge ics">ICS</span> {fmtDate(c.ticketLastStatusDate)}</>
                                                            : <><span className="date-badge ticket">Ticket</span> {fmtDate(c.creationDate)}</>
                                                        }
                                                    </dd>
                                                    <dt>Descrição Status (ICS)</dt><dd>{c.ticketStatusDescriptionUpdate || '—'}</dd>
                                                    <dt>Descrição Status</dt><dd>{c.statusDescription || '—'}</dd>
                                                    <dt>Descrição Ticket</dt><dd>{c.ticketDescription || '—'}</dd>
                                                </dl>
                                            </div>

                                            {foiBipado && (
                                                <div className="detail-section detail-section-bipado">
                                                    <h4>Bipagem</h4>
                                                    <dl>
                                                        <dt>Operador</dt><dd>{item.operator || '—'}</dd>
                                                        <dt>Último Registro</dt><dd>{item.lastRecord || '—'}</dd>
                                                        <dt>Status Atual</dt><dd>{item.currentStatus || '—'}</dd>
                                                        <dt>Data da Bipagem</dt>
                                                        <dd>
                                                            {item.modificationHistory?.length > 0
                                                                ? fmtDate(item.modificationHistory[item.modificationHistory.length - 1].timestamp)
                                                                : '—'
                                                            }
                                                        </dd>
                                                    </dl>

                                                    {item.modificationHistory?.length > 0 && (
                                                        <div className="mod-history">
                                                            <h5>Histórico de Registros</h5>
                                                            <div className="mod-list">
                                                                {[...item.modificationHistory].reverse().map((m, i) => (
                                                                    <div className="mod-entry" key={i}>
                                                                        <span className="mod-time">{fmtDate(m.timestamp)}</span>
                                                                        <span className="mod-user">{m.user || m.action || '—'}</span>
                                                                        {m.location && <span className="mod-loc">{m.location}</span>}
                                                                        {m.observation && <span className="mod-obs">{m.observation}</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            <div className="detail-section detail-section-sys">
                                                <h4>Sistema</h4>
                                                <dl>
                                                    <dt>Última atualização dos dados</dt><dd>{fmtDate(c.updatedAt)}</dd>
                                                </dl>
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ─── JSX ─────────────────────────────────────────────────────────────────

    const tabs = [
        { id: 'resumo',     label: 'Resumo',        icon: '📊' },
        { id: 'tickets', label: 'Tickets', icon: '🎫', count: analytics.geral?.totalConjuntos },
        { id: 'assunto',    label: 'Por Assunto',    icon: '📋', count: Object.keys(analytics.porAssunto).length },
        { id: 'embarcador', label: 'Por Embarcador', icon: '🏢', count: Object.keys(analytics.porEmbarcador).length },
        { id: 'rota',       label: 'Por Rota',       icon: '🚚', count: Object.keys(analytics.porRota).length },
        { id: 'naoLoc',     label: 'Não Localizados',icon: '🔍', count: g.totalNaoLocalizados, highlight: g.totalNaoLocalizados > 0 },
    ];

    return (
        <div id="Analytics">
            <BarMenu />
            <section className="analytics-section">

                {/* ── HEADER ─────────────────────────────────────────── */}
                <div className="an-header">
                    <div>
                        <h1>Analytics</h1>
                        <p className="an-subtitle">Localização do estoque</p>
                    </div>
                    <button className="btn-refresh" onClick={fetchAnalytics}>↻ Atualizar</button>
                </div>

                {/* ── KPI CARDS ──────────────────────────────────────── */}
                <div className="kpi-row">
                    <div className="kpi-card kpi-blue">
                        <span className="kpi-label">Total de Itens</span>
                        <span className="kpi-value">{fmtNum(g.totalConjuntos)}</span>
                    </div>
                    <div className="kpi-card kpi-green">
                        <span className="kpi-label">Localizados</span>
                        <span className="kpi-value">{fmtNum(g.totalBipados)}</span>
                        <span className="kpi-sub">{fmtPct(g.percentualBipado)} do total</span>
                    </div>
                    <div className="kpi-card kpi-orange">
                        <span className="kpi-label">Não Localizados</span>
                        <span className="kpi-value">{fmtNum(g.totalNaoBipados)}</span>
                    </div>
                    <div className="kpi-card kpi-taxa" style={{ '--pct': bipPct, '--color': pctColor(bipPct) }}>
                        <span className="kpi-label">Taxa de Localizados</span>
                        <span className="kpi-value kpi-taxa-num" style={{ color: pctColor(bipPct) }}>
                            {fmtPct(bipPct)}
                        </span>
                        <div className="kpi-taxa-bar">
                            <div className="kpi-taxa-fill" style={{ width: `${bipPct}%`, background: pctColor(bipPct) }} />
                        </div>
                    </div>
                    <div className={`kpi-card ${g.totalNaoLocalizados > 0 ? 'kpi-red' : 'kpi-neutral'}`}>
                        <span className="kpi-label">Não Localizados - Estoque</span>
                        <span className="kpi-value">{fmtNum(g.totalNaoLocalizados)}</span>
                        <span className="kpi-sub">Em estoque · não bipados</span>
                    </div>
                    {g.itensNaoCatalogados > 0 && (
                        <div className="kpi-card kpi-purple">
                            <span className="kpi-label">Não Catalogados</span>
                            <span className="kpi-value">{fmtNum(g.itensNaoCatalogados)}</span>
                            <span className="kpi-sub">Bipados fora da lista</span>
                        </div>
                    )}
                </div>

                {/* ── FILTERS ────────────────────────────────────────── */}
                {activeTab !== 'naoLoc' && activeTab !== 'tickets' && (
                    <div className="filters-panel">
                        <div className="filters-row">
                            <div className="filter-group filter-search">
                                <label>Buscar</label>
                                <div className="search-input-wrap">
                                    <span className="search-icon">🔍</span>
                                    <input
                                        type="text"
                                        placeholder="Nome"
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                    {search && <button className="btn-clear-inline" onClick={() => setSearch('')}>✕</button>}
                                </div>
                            </div>

                            <div className="filter-group filter-date">
                                <label>Data de criação</label>
                                <div className="date-range">
                                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} placeholder="De" />
                                    <span>até</span>
                                    <input type="date" value={dateTo}   onChange={e => setDateTo(e.target.value)}   placeholder="Até" />
                                </div>
                            </div>

                            {activeTab !== 'resumo' && (
                                <div className="filter-group filter-stock">
                                    <label>Status de Estoque</label>
                                    <div className="stock-btns">
                                        {['Fora Estoque', 'Em Estoque', 'Outros'].map(s => (
                                            <button
                                                key={s}
                                                className={`stock-btn ${stockFilter === s ? 'active' : ''}`}
                                                onClick={() => setStockFilter(prev => prev === s ? null : s)}
                                            >
                                                {s === 'Fora Estoque' ? '📦' : s === 'Em Estoque' ? '✓' : '?'} {s}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {hasFilters && (
                                <button className="btn-clear-all" onClick={clearFilters}>✕ Limpar filtros</button>
                            )}
                        </div>

                        {/* active filter tags */}
                        {hasFilters && (
                            <div className="filter-tags">
                                {search && (
                                    <span className="ftag">🔍 "{search}" <button onClick={() => setSearch('')}>✕</button></span>
                                )}
                                {dateFrom && (
                                    <span className="ftag">📅 De {new Date(dateFrom + 'T12:00:00').toLocaleDateString('pt-BR')} <button onClick={() => setDateFrom('')}>✕</button></span>
                                )}
                                {dateTo && (
                                    <span className="ftag">📅 Até {new Date(dateTo + 'T12:00:00').toLocaleDateString('pt-BR')} <button onClick={() => setDateTo('')}>✕</button></span>
                                )}
                                {stockFilter && (
                                    <span className="ftag">📦 {stockFilter} <button onClick={() => setStockFilter(null)}>✕</button></span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* ── TABS ───────────────────────────────────────────── */}
                <div className="tabs-bar">
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            className={`tab-btn ${activeTab === t.id ? 'active' : ''} ${t.highlight ? 'highlight' : ''}`}
                            onClick={() => setActiveTab(t.id)}
                        >
                            <span>{t.icon} {t.label}</span>
                            {t.count != null && (
                                <span className={`tab-count ${t.highlight ? 'tab-count-red' : ''}`}>{fmtNum(t.count)}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* ── TAB CONTENT ────────────────────────────────────── */}
                <div className="tab-content">
                    {activeTab === 'resumo'     && renderResumo()}
                    {activeTab === 'tickets' && renderTickets()}
                    {activeTab === 'assunto'    && renderDataTable(analytics.porAssunto,    'Assunto')}
                    {activeTab === 'embarcador' && renderDataTable(analytics.porEmbarcador, 'Embarcador')}
                    {activeTab === 'rota'       && renderDataTable(analytics.porRota,       'Rota')}
                    {activeTab === 'naoLoc'     && renderNaoLocalizados()}
                </div>

            </section>
        </div>
    );
}
